import { getDollarPriceStringFromCents } from "lib";

// Discriminated union types for billing document activity entries
interface BillingDocumentActivityBase {
  billingDocumentId: string;
  date: string;
  key: string;
  title: string;
}

interface CreatedActivity extends BillingDocumentActivityBase {
  key: "created";
  id: string;
  invoiceNumber: string | null;
}

interface PaymentActivity extends BillingDocumentActivityBase {
  key: "payments" | "creditNotes";
  totalAmountCents: number;
  invoiceNumber: string | null;
}

interface RetainedProceedsActivity extends BillingDocumentActivityBase {
  key: "retainedProceeds";
  invoiceNumber: string | null;
}

interface ReversalActivity extends BillingDocumentActivityBase {
  key: "reversal";
  invoiceNumber: string | null;
}

interface InterestActivity extends BillingDocumentActivityBase {
  key: "interest";
  note: string;
}

interface ModifiedActivity extends BillingDocumentActivityBase {
  key: "modified";
  historyChangeReason: string | null;
}

export type BillingDocumentActivity =
  | CreatedActivity
  | PaymentActivity
  | RetainedProceedsActivity
  | ReversalActivity
  | InterestActivity
  | ModifiedActivity;

export const billingDocumentActivityTitleAndColorMap = {
  created: {
    color: "billingDocumentActivityCreated",
    title: "Created",
    textColor: "black",
  },
  creditNotes: {
    color: "billingDocumentActivityPayment",
    title: "Credit Note",
    textColor: "white",
  },
  retainedProceeds: {
    color: "billingDocumentActivityModified",
    title: "Retained Proceeds",
    textColor: "white",
  },
  payments: {
    color: "billingDocumentActivityPayment",
    title: "Payment",
    textColor: "white",
  },
  interest: {
    color: "billingDocumentActivityInterest",
    title: "Interest",
    textColor: "white",
  },
  reversal: {
    color: "billingDocumentActivityReversal",
    title: "Reversal",
    textColor: "white",
  },
  modified: {
    color: "billingDocumentActivityModified",
    title: "Modified",
    textColor: "white",
  },
};

// Activity keys that correspond to an actual generated billing document.
// "interest" and "modified" are plain history/audit entries with no
// document behind them, so they must never show a View button.
export const DOCUMENT_ACTIVITY_KEYS: ReadonlyArray<BillingDocumentActivity["key"]> = [
  "created",
  "payments",
  "creditNotes",
  "retainedProceeds",
  "reversal",
];

export const activityDetailsMap = (
  activity: BillingDocumentActivity,
): Record<string, string> => {
  const result: Record<string, string> = {};

  if (activity.key === "created") {
    result.created = `${activity.invoiceNumber} Created`;
  } else if (activity.key === "payments") {
    result.payments = `$${getDollarPriceStringFromCents(
      activity.totalAmountCents,
    )} - ${activity.invoiceNumber} `;
  } else if (activity.key === "creditNotes") {
    result.creditNotes = `$${getDollarPriceStringFromCents(
      activity.totalAmountCents,
    )} - ${activity.invoiceNumber}`;
  } else if (activity.key === "retainedProceeds") {
    result.retainedProceeds = `Retained Proceeds from ${activity.invoiceNumber}`;
  } else if (activity.key === "reversal") {
    result.reversal = `Reversed by ${activity.invoiceNumber}`;
  } else if (activity.key === "interest") {
    result.interest = activity.note;
  } else if (activity.key === "modified") {
    result.modified = activity.historyChangeReason || "Document modified";
  }

  return result;
};
